import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  TeacherHubHeader,
  TeacherHubTabs,
  RecentWorksPanel,
  TeacherCreationTab,
  TeacherSubjectsTab,
  TeacherJournalTab,
  TeacherClassManagementTab,
  TeacherResourcesTab,
  TeacherSharedResourcesTab,
} from "../components/teacher-hub";
import { CartableModule } from "../components/teacher-hub/CartableModule";
import {
  listResources,
  duplicateResource,
  shareResource,
  getTeacherZone,
} from "../lib/api";
import { Toaster, toast } from "sonner";

export default function TeacherHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("creation");
  const [recentResources, setRecentResources] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [teacherCycles, setTeacherCycles] = useState([]);

  useEffect(() => {
    // Redirect if not teacher
    if (user && user.role !== "teacher") {
      navigate("/home");
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchRecentResources();
    fetchTeacherZone();
  }, []);

  const fetchRecentResources = async () => {
    try {
      const { data } = await listResources({ limit: 10 });
      setRecentResources(data);
    } catch (err) {
      console.error("Error fetching recent resources:", err);
    } finally {
      setLoadingRecent(false);
    }
  };

  const fetchTeacherZone = async () => {
    try {
      const { data } = await getTeacherZone();
      if (data?.cycles) {
        setTeacherCycles(data.cycles);
      }
    } catch (err) {
      console.error("Error fetching teacher zone:", err);
    }
  };

  const handleDuplicate = async (resource) => {
    try {
      const { data } = await duplicateResource(resource.id);
      toast.success("Ressource dupliquee !");
      fetchRecentResources();
      // Navigate to duplicated resource
      if (data.elements) {
        navigate(`/create?id=${data.id}`);
      } else {
        navigate(`/editor/${data.id}`);
      }
    } catch (err) {
      toast.error("Erreur lors de la duplication");
    }
  };

  const handleSchedule = (resource) => {
    // Switch to journal tab with resource preselected
    setActiveTab("journal");
    toast.info(`Planifiez "${resource.title}" dans le cahier-journal`);
  };

  const handleShare = async (resource) => {
    try {
      await shareResource(resource.id);
      toast.success("Ressource partagee avec la communaute !");
      fetchRecentResources();
    } catch (err) {
      toast.error("Erreur lors du partage");
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "creation":
        return <TeacherCreationTab />;
      case "matieres":
        return <TeacherSubjectsTab />;
      case "journal":
        return <TeacherJournalTab />;
      case "classe":
        return <TeacherClassManagementTab />;
      case "cartable":
        return <CartableModule teacherCycles={teacherCycles} />;
      case "ressources":
        return <TeacherResourcesTab />;
      case "partage":
        return <TeacherSharedResourcesTab />;
      default:
        return <TeacherCreationTab />;
    }
  };

  if (!user || user.role !== "teacher") {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
      <Toaster position="top-center" richColors />
      
      <TeacherHubHeader />
      <TeacherHubTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Welcome Banner - Only on creation tab */}
        {activeTab === "creation" && (
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-2">
              <img
                src="/LEcoleDeNavi2.png"
                alt="Navi"
                className="h-14 md:h-16 mascot-float"
              />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-800">
                  Bonjour {user?.name} !
                </h1>
                <p className="text-slate-500 text-sm">
                  Que souhaitez-vous preparer aujourd'hui ?
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Recent Works Panel - Show on creation tab */}
        {activeTab === "creation" && recentResources.length > 0 && (
          <RecentWorksPanel
            resources={recentResources}
            loading={loadingRecent}
            onDuplicate={handleDuplicate}
            onSchedule={handleSchedule}
            onShare={handleShare}
          />
        )}

        {/* Tab Content */}
        {renderTabContent()}
      </main>

      {/* Footer decoration */}
      <div className="fixed bottom-4 right-4 opacity-20 pointer-events-none hidden lg:block">
        <img
          src="/LEcoleDeNavi4.png"
          alt=""
          className="h-24"
        />
      </div>
    </div>
  );
}
